import {
  Button,
  Divider,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackDivider,
} from '@chakra-ui/react';
import * as React from 'react';

import { FieldValidationError } from '../../../../client/legacy-shared-modules/csv/csv-field-mapping';
import { IAction } from '../../admin/layouts/page-layout/page-header/IAction';
import { ICsvFileDropProps } from './CsvFileDrop';
import {
  CsvImportAccountCreationConfiguration,
  ICsvImportAccountCreationConfigurationProps,
} from './CsvImportAccountCreationConfiguration';
import { ICsvImportConfigurationProps } from './CsvImportConfiguration';
import { CsvImportModalFrameImportContent } from './CsvImportModalFrameImportContent';
import {
  RemoveAllOtherGroupMembersToggle,
  RemoveAllOtherGroupMembersToggleProps,
} from './CsvImportRemoveOtherGroupMembersToggle';
import {
  SendEmailInvites,
  SendEmailInvitesProps,
} from './CsvImportSendEmailToggle';
import {
  CsvImportTemplate,
  ICsvImportTemplateProps,
} from './CsvImportTemplate';

type ICsvImportModalFrameProps<FieldMapping> = {
  pluralEntityName: string;
  modalTitle?: string;
  primaryActionLabel?: string;
  footerProps: {
    primaryAction: IAction;
    secondaryAction?: IAction;
  };
  importConfig?: ICsvImportConfigurationProps;
  validationErrors: FieldValidationError | null;
  template?: ICsvImportTemplateProps<FieldMapping>;
  hasLoadingSideEffect: boolean;
  onRemoveFile: (e: React.SyntheticEvent) => void;
  note?: string;
  removeAllOtherGroupMembersToggleProps?: RemoveAllOtherGroupMembersToggleProps;
  csvImportAccountCreationConfigurationProps?: ICsvImportAccountCreationConfigurationProps;
  sendEmailInvitesProps?: SendEmailInvitesProps;
} & ICsvFileDropProps;

export function CsvImportModalFrameEnhanced<FieldMapping>(
  props: React.PropsWithChildren<ICsvImportModalFrameProps<FieldMapping>>
): React.ReactElement | null {
  const showCsvImportAccountCreationConfiguration =
    !!props.csvImportAccountCreationConfigurationProps;
  const showSendEmailInvites =
    props.sendEmailInvitesProps &&
    (!props.csvImportAccountCreationConfigurationProps ||
      props.csvImportAccountCreationConfigurationProps.shouldCreateAccounts);

  return (
    <>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {props.modalTitle ?? `Bulk Import ${props.pluralEntityName}`}
        </ModalHeader>

        <Divider />

        <ModalCloseButton />

        <ModalBody as={Stack} spacing="6" p="6">
          <CsvImportModalFrameImportContent
            validationErrors={props.validationErrors}
            importConfig={props.importConfig}
            file={props.file}
            onFileChange={props.onFileChange}
            hasLoadingSideEffect={props.hasLoadingSideEffect}
            onRemoveFile={props.onRemoveFile}
            note={props.note}
          />

          <Stack
            spacing="6"
            p="4"
            bg="gray.50"
            borderRadius="xl"
            divider={<StackDivider />}
          >
            {(showCsvImportAccountCreationConfiguration ||
              showSendEmailInvites) && (
              <Stack spacing="6">
                {showCsvImportAccountCreationConfiguration &&
                  props.csvImportAccountCreationConfigurationProps && (
                    <CsvImportAccountCreationConfiguration
                      {...props.csvImportAccountCreationConfigurationProps}
                    />
                  )}

                {showSendEmailInvites && props.sendEmailInvitesProps && (
                  <SendEmailInvites {...props.sendEmailInvitesProps} />
                )}
              </Stack>
            )}

            {props.removeAllOtherGroupMembersToggleProps && (
              <RemoveAllOtherGroupMembersToggle
                {...props.removeAllOtherGroupMembersToggleProps}
              />
            )}
          </Stack>
        </ModalBody>

        <Divider />

        <ModalFooter gap="4" justifyContent="space-between">
          {props.template && (
            <Button
              as={CsvImportTemplate}
              variant="outline"
              colorScheme="gray"
              {...props.template}
            >
              Download CSV Template
            </Button>
          )}

          <Button
            isLoading={props.footerProps.primaryAction.loading}
            isDisabled={props.footerProps.primaryAction.disabled}
            onClick={props.footerProps.primaryAction.onClick}
          >
            {props.footerProps.primaryAction.content ??
              props.primaryActionLabel ??
              'Start Import'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </>
  );
}
